import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import featureTogglesService from '@auspost/auspost-feature-toggles';
import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent } from 'App/common/Notification/Notification.config';
import { myPostBaseUrl } from 'config/track-config';
import { anonCustomer, Customer } from 'models/Customer';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { AppAsyncThunkOpts } from 'models/Store';
import { createNotification } from 'store/notification';
import { AppDispatch } from 'store/store';
import { reportNewRelicError } from 'utils/window.utils';

import { getAccessToken } from '../../interceptors/auth0.interceptor';
import { authService } from './utils/auth0.util';
import eligiblityUtil from './utils/eligiblity.util';

const DISABLE_AUTHENTICATED = 'track-disable-authenticated';

export const getCustomer = createAsyncThunk<Customer, string | undefined | null, AppAsyncThunkOpts>(
  'customer/getCustomer',
  async (guid, thunkAPI) => {
    try {
      const disableAuth = await featureTogglesService.isOn(DISABLE_AUTHENTICATED);
      if (disableAuth) return anonCustomer;
      if (!authService) return anonCustomer;

      const isAuthed = await authService?.isAuthenticated();
      if (!isAuthed && guid) {
        return {
          ...anonCustomer,
          knownToken: guid,
        };
      } else if (!isAuthed) {
        return anonCustomer;
      }

      // Get accessToken
      const accessToken = await getAccessToken();

      // Check eligibility
      try {
        if (!accessToken) return anonCustomer;
        const customerSetupVerified = await eligiblityUtil.verifyCustomerSetup();
        if (!customerSetupVerified) return anonCustomer;
      } catch (err) {
        reportNewRelicError(err);
        return anonCustomer;
      }

      // getCustomer info
      try {
        const customerInfo = await authService?.getUser();
        const authedCustomer: Customer = {
          isAuthenticated: true,
          info: customerInfo,
        };
        return authedCustomer;
      } catch (error) {
        console.error(error);
        return anonCustomer;
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const axiosErr = err as AxiosError;
        const statusCode = axiosErr.response?.status;
        if (statusCode) handleCustomerError(thunkAPI.dispatch, statusCode);
        return thunkAPI.rejectWithValue({
          status: axiosErr.response?.status,
          data: axiosErr.response?.data,
          isAxiosError: axiosErr.isAxiosError,
        });
      }

      const authError = err as AuthError;
      if (
        (authError.isReauthRequiredError && authError.isReauthRequiredError()) ||
        (authError.isLoginRequiredError && authError.isLoginRequiredError())
      ) {
        thunkAPI.dispatch(deleteSession());
      }
      throw err;
    }
  }
);

export const deleteSession = createAsyncThunk<Customer, boolean | undefined, AppAsyncThunkOpts>(
  'customer/deleteSession',
  async (logout = true) => {
    if (logout) {
      // this url is not used, we are preventing redirection using the the skipRedirection flag
      await authService?.logout(myPostBaseUrl + '/track/', true);
    }
    return anonCustomer;
  }
);

function handleCustomerError(dispatch: AppDispatch, statusCode: number) {
  if (statusCode === 403) {
    const notification: Notification = {
      type: NotificationType.ALERT,
      status: NotificationStatus.ERROR,
      message: errorContent.unauthorisedKnownToken,
    };
    dispatch(createNotification(notification));
  }
}
